import React, { useState, useRef } from 'react';

// Connexion réservée aux comptes Microsoft 365 de Dargency (audit sécurité, octobre 2026) :
// les connexions Google et par lien email ont été retirées.
function LoginPage({ onLogin, microsoftClientId, microsoftTenantId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const msalInstanceRef = useRef(null);

  // Initialisation MSAL à la demande (au clic)
  const ensureMsalReady = async () => {
    if (msalInstanceRef.current) return msalInstanceRef.current;
    if (!window.msal) {
      // Charger le script dynamiquement si pas encore chargé
      await new Promise((resolve, reject) => {
        if (window.msal) return resolve();
        const script = document.createElement('script');
        script.src = 'https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    const instance = new window.msal.PublicClientApplication({
      auth: {
        clientId: microsoftClientId,
        // Annuaire Dargency si connu, sinon comptes professionnels uniquement (pas de compte perso).
        authority: `https://login.microsoftonline.com/${microsoftTenantId || 'organizations'}`,
        redirectUri: window.location.origin
      },
      cache: { cacheLocation: 'sessionStorage' }
    });
    await instance.initialize();
    msalInstanceRef.current = instance;
    return instance;
  };

  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const msalInstance = await ensureMsalReady();
      const result = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account'
      });

      const res = await fetch('/api/auth/microsoft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: result.idToken })
      });
      const data = await res.json();
      if (data.token) {
        onLogin(data.token, data.user);
      } else {
        setError(data.error || 'Erreur de connexion Microsoft');
      }
    } catch (err) {
      if (err.errorCode !== 'user_cancelled') {
        console.error('Erreur Microsoft:', err);
        setError('Erreur de connexion Microsoft');
      }
    } finally {
      setLoading(false);
    }
  };

  const microsoftButtonStyle = {
    width: '100%',
    maxWidth: '360px',
    padding: '10px 16px',
    borderRadius: '4px',
    border: '1px solid #8c8c8c',
    background: '#ffffff',
    color: '#5e5e5e',
    fontSize: '14px',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontFamily: "'Segoe UI', 'Inter', sans-serif",
    transition: 'background 0.2s',
    margin: '0 auto'
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '440px',
        width: '100%',
        textAlign: 'center'
      }}>
        <img
          src="/logo_dargency.jpg"
          alt="DARGENCY"
          style={{ height: '48px', marginBottom: '24px', borderRadius: '8px' }}
        />
        <h1 style={{
          color: '#1e293b',
          fontSize: '28px',
          fontWeight: '700',
          marginBottom: '8px'
        }}>
          AssurExtract
        </h1>
        <p style={{
          color: '#64748b',
          fontSize: '14px',
          marginBottom: '32px'
        }}>
          Accès réservé à l'équipe Dargency (compte Microsoft 365)
        </p>

        {loading && (
          <p style={{ color: '#60a5fa', fontSize: '14px', marginBottom: '16px' }}>
            Connexion en cours...
          </p>
        )}

        {error && (
          <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </p>
        )}

        {/* Bouton Microsoft Sign-In */}
        {microsoftClientId && (
          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={handleMicrosoftLogin}
              disabled={loading}
              style={microsoftButtonStyle}
              onMouseEnter={(e) => { if (!loading) e.target.style.background = '#f5f5f5'; }}
              onMouseLeave={(e) => { e.target.style.background = '#ffffff'; }}
            >
              <svg width="21" height="21" viewBox="0 0 21 21" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Se connecter avec Microsoft
            </button>
          </div>
        )}

        {!microsoftClientId && (
          <p style={{ color: '#64748b', fontSize: '13px' }}>
            Connexion Microsoft non configurée sur le serveur.
          </p>
        )}

        <p style={{
          color: '#475569',
          fontSize: '12px',
          marginTop: '24px'
        }}>
          Propulsé par DARGENCY
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
